import { useQuery } from '@tanstack/react-query'
import { useParams, useSearch } from '@tanstack/react-router'
import { ArrowLeft, Plus } from 'lucide-react'
import { useState } from 'react'

import { savingsAccountListQueryOptions } from '@/api/savings-api'
import { useBranchSelection } from '@/components/app-shell/use-branch-selection'
import { ErrorSurface } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { TabStrip, type TabDefinition } from '@/components/tab-strip'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { productClassLabel } from '@/features/savings/constants'
import { savingsPaths, useSavingsNavigate } from '@/features/savings/navigation'
import { OpenAccountDialog } from '@/features/savings/open-account-dialog'
import { PassbooksPanel } from '@/features/savings/passbook-panels'
import { useSavingsPermissions } from '@/features/savings/permissions'
import { LoanPrerequisitesPanel } from '@/features/savings/prerequisites-panel'

import { savingsCustomerSearchSchema, type SavingsCustomerTab } from './search'

const PANEL_ID = 'customer-savings-panel'

/**
 * One customer's savings position: their accounts, their passbooks, and the three gates a loan
 * account cannot be opened without.
 *
 * The three sit together because they are one question asked from three angles at the counter —
 * "can this member take a loan?" — and because two of the three answers live nowhere else in the
 * application. The passbook register and the prerequisite check are both keyed by customer rather
 * than by account, which is why they are here and not on an account.
 *
 * Read-mostly, so it works at any width. Issuing a book is a registration, not a payment; nothing
 * on this screen keys an amount except the optional loan requirement used to judge a deposit, which
 * moves no money at all.
 */
export function CustomerSavingsPage() {
  const params: Record<string, string | undefined> = useParams({ strict: false })
  const customerId = params.customerId ?? ''
  const navigate = useSavingsNavigate()
  const permissions = useSavingsPermissions()
  const { selectedBranchId } = useBranchSelection()

  const search = savingsCustomerSearchSchema.parse(useSearch({ strict: false }))
  const tab: SavingsCustomerTab = search.tab ?? 'accounts'

  const [opening, setOpening] = useState(false)

  const accounts = useQuery(
    savingsAccountListQueryOptions({ customerId, page: 1, pageSize: 50 }),
  )

  const selectTab = (next: SavingsCustomerTab) => {
    navigate(savingsPaths.customer(customerId), { search: { tab: next }, replace: true })
  }

  // The list carries the customer's name on every row, so the header can be theirs without a
  // second request to the customers module — and it degrades to the identifier rather than to a
  // blank when they hold no account yet.
  const customerName = accounts.data?.items[0]?.customerName
  const customerNumber = accounts.data?.items[0]?.customerNumber

  return (
    <>
      <button
        type="button"
        onClick={() => navigate(savingsPaths.accounts())}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover max-lg:min-h-11 max-lg:py-2"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        All savings accounts
      </button>

      <PageHeader
        title={customerName ?? 'Customer savings'}
        reference={customerNumber}
        description="Accounts, passbooks, and the loan gates — the three things the savings module knows about a customer."
        actions={
          permissions.canOpenAccount ? (
            <Button variant="primary" onClick={() => setOpening(true)}>
              <Plus />
              Open an account
            </Button>
          ) : null
        }
      />

      <div className="space-y-4">
        <TabStrip<SavingsCustomerTab>
          tabs={
            [
              { id: 'accounts', label: 'Accounts', badge: accounts.data?.totalCount },
              { id: 'passbooks', label: 'Passbooks' },
              { id: 'prerequisites', label: 'Loan gates' },
            ] satisfies TabDefinition<SavingsCustomerTab>[]
          }
          active={tab}
          onChange={selectTab}
          panelId={PANEL_ID}
          label="Customer savings sections"
        />

        <div id={PANEL_ID} role="tabpanel" aria-labelledby={`${PANEL_ID}-tab-${tab}`}>
          {tab === 'accounts' ? (
            accounts.isPending ? (
              <div className="space-y-2">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            ) : accounts.isError ? (
              <ErrorSurface
                error={accounts.error}
                onRetry={() => void accounts.refetch()}
                title="This customer's accounts could not be read"
              />
            ) : accounts.data.items.length === 0 ? (
              <Card>
                <CardContent className="py-10 text-center">
                  <p className="text-sm font-medium text-heading">No savings account</p>
                  <p className="mx-auto mt-1 max-w-md text-xs text-muted">
                    This customer holds none at a branch you can see. A loan account cannot be
                    opened without one, along with a security deposit account and a passbook.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <ul className="grid gap-3 lg:grid-cols-2">
                {accounts.data.items.map((account) => (
                  <li key={account.id}>
                    <Card>
                      <CardHeader>
                        <div className="min-w-0 space-y-1">
                          <CardTitle className="font-mono">{account.accountNumber}</CardTitle>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <StatusBadge status={account.status} />
                            <Badge
                              tone={
                                account.productClass === 'SecurityDeposit' ? 'warning' : 'neutral'
                              }
                            >
                              {productClassLabel(account.productClass)}
                            </Badge>
                          </div>
                        </div>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(savingsPaths.account(account.id))}
                        >
                          Open
                        </Button>
                      </CardHeader>
                      <CardContent>
                        <dl className="grid grid-cols-3 gap-3">
                          <div>
                            <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">
                              Ledger
                            </dt>
                            <dd className="mt-0.5">
                              <MoneyDisplay
                                value={account.ledgerBalanceMinor}
                                currency={account.currencyCode}
                                symbol={false}
                                className="text-left"
                              />
                            </dd>
                          </div>
                          <div>
                            <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">
                              On hold
                            </dt>
                            <dd className="mt-0.5">
                              <MoneyDisplay
                                value={account.holdAmountMinor}
                                currency={account.currencyCode}
                                symbol={false}
                                emphasis={account.holdAmountMinor > 0 ? 'normal' : 'muted'}
                                className="text-left"
                              />
                            </dd>
                          </div>
                          <div>
                            <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">
                              Available
                            </dt>
                            <dd className="mt-0.5">
                              <MoneyDisplay
                                value={account.availableBalanceMinor}
                                currency={account.currencyCode}
                                symbol={false}
                                emphasis="strong"
                                className="text-left"
                              />
                            </dd>
                          </div>
                        </dl>
                        {!account.isWithdrawable ? (
                          <p className="mt-2 text-2xs text-muted">
                            Collateral. Not withdrawable by anybody — it leaves by a countersigned
                            offset or by closing the account.
                          </p>
                        ) : null}
                      </CardContent>
                    </Card>
                  </li>
                ))}
              </ul>
            )
          ) : null}

          {tab === 'passbooks' ? (
            <PassbooksPanel customerId={customerId} branchId={selectedBranchId} />
          ) : null}

          {tab === 'prerequisites' ? <LoanPrerequisitesPanel customerId={customerId} /> : null}
        </div>
      </div>

      <OpenAccountDialog
        customerId={customerId}
        branchId={selectedBranchId}
        open={opening}
        onOpenChange={setOpening}
      />
    </>
  )
}
